import classNames from 'classnames';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import Dialog from '@mui/material/Dialog';

import type { RootState } from 'store';

import { AccountScreen } from 'common/accountActivationAndRecovery/AccountScreen';
import { AuthActions } from 'common/auth/actions';
import { DeviceHandoffExitLink } from 'common/deviceHandoff/components/DeviceHandoffExitLink';
import {
    selectIsDeviceHandoffModalOpen,
    selectIsRespondentAcceptingPolicy,
    selectIsSwitchingToRespondent,
    selectRespondentTarget,
} from 'common/deviceHandoff/state/selectors';
import { CIBlock } from 'common/routing/blocking/ci-non-route-block';
import { LoadingOverlay } from 'common/utils/loading';

import './deviceHandoffModal.scss';
import { deviceHandoffScreenPath } from './deviceHandoffScreenPaths';
import { useBlockNavigationDuringDeviceHandoff } from './hooks/useBlockNavigationDuringDeviceHandoff';

const keepModalOpen = (): void => undefined;

export const DeviceHandoffDialog = (): ReactElement => {
    const { t } = useTranslation('global');
    useTranslation(['auth', 'policy', 'user']);
    const dispatch = useDispatch();
    const { pathname } = useLocation();

    const isDeviceHandoffModalOpen = useSelector(selectIsDeviceHandoffModalOpen);
    const isSwitchingToRespondent = useSelector(selectIsSwitchingToRespondent);
    const isRespondentAcceptingPolicy = useSelector(selectIsRespondentAcceptingPolicy);
    const respondentTarget = useSelector(selectRespondentTarget);
    const ssoLoading = useSelector((state: RootState) => state.auth.ssoLoading);
    const handoffPhase = useSelector((state: RootState) => state.deviceHandoff.phase);

    const { isLeaveConfirmationVisible, confirmLeave, cancelLeave } = useBlockNavigationDuringDeviceHandoff(() => {
        dispatch(AuthActions.logoutRequest());
    });

    if (!isDeviceHandoffModalOpen) {
        return <Navigate to="/" replace />;
    }

    const policyPath = deviceHandoffScreenPath(AccountScreen.Policy);
    const isPolicyScreen = pathname === policyPath;

    if (isRespondentAcceptingPolicy && !isPolicyScreen) {
        return <Navigate to={policyPath} replace />;
    }

    const isRespondentActivated = respondentTarget?.activationStatus;

    return (
        <>
            <Dialog
                onClose={keepModalOpen}
                aria-labelledby="switch-user-modal"
                open
                className={classNames('switch-user-dialog-window', {
                    expand_panel: pathname === deviceHandoffScreenPath(AccountScreen.CreatePassword),
                    expand_panel_policy: isPolicyScreen,
                    expand_user_registration: pathname === deviceHandoffScreenPath(AccountScreen.Activation),
                })}
            >
                <LoadingOverlay isLoading={ssoLoading}>
                    <div
                        id="switch_user_dialog_content"
                        className={isSwitchingToRespondent && !isRespondentActivated ? 'registration_option' : ''}
                        data-handoff-phase={handoffPhase}
                    >
                        <div id="dynamic_switch_user_content">
                            <Outlet />
                        </div>
                        {!isPolicyScreen && (
                            <DeviceHandoffExitLink
                                label={isSwitchingToRespondent ? t('switchuser.log_out') : t('switchuser.sign_out_all')}
                                isRespondentSession={isSwitchingToRespondent}
                            />
                        )}
                    </div>
                </LoadingOverlay>
            </Dialog>
            <CIBlock
                showBlock={isLeaveConfirmationVisible}
                dialogParts={{
                    title: t('switchuser.navigation.title'),
                    message: t('switchuser.navigation.message'),
                    confirmButtonText: t('switchuser.navigation.confirmButtonText'),
                    cancelButtonText: t('switchuser.navigation.cancelButtonText'),
                }}
                stayCallback={cancelLeave}
                goCallback={confirmLeave}
            />
        </>
    );
};
